import { useState } from 'react'
import { PlusIcon, TrashIcon, UserGroupIcon, CalculatorIcon, BanknotesIcon, TruckIcon, SparklesIcon, ReceiptPercentIcon, CameraIcon, PhotoIcon } from '@heroicons/react/24/outline'
import { motion, AnimatePresence } from 'framer-motion'
import Footer from '../components/Footer'

export default function SplitBill() {
  const [totalBill, setTotalBill] = useState('')
  const [tip, setTip] = useState('')
  const [tax, setTax] = useState('')
  const [shipping, setShipping] = useState('')
  const [people, setPeople] = useState([
    { id: 1, name: '', amount: '', items: [] }
  ])
  const [splitMethod, setSplitMethod] = useState('equal') // equal, custom, percentage, detailed
  const [uploadedImage, setUploadedImage] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [ocrResult, setOcrResult] = useState(null)

  const addPerson = () => {
    setPeople([...people, { id: Date.now(), name: '', amount: '', items: [] }])
  }

  const removePerson = (id) => {
    if (people.length > 1) {
      setPeople(people.filter(person => person.id !== id))
    }
  }

  const updatePerson = (id, field, value) => {
    setPeople(people.map(person => 
      person.id === id ? { ...person, [field]: value } : person
    ))
  }

  // New functions for detailed items
  const addItemToPerson = (personId) => {
    setPeople(people.map(person => 
      person.id === personId 
        ? { ...person, items: [...(person.items || []), { id: Date.now(), name: '', price: '' }] }
        : person
    ))
  }

  const removeItemFromPerson = (personId, itemId) => {
    setPeople(people.map(person => 
      person.id === personId 
        ? { ...person, items: person.items.filter(item => item.id !== itemId) }
        : person
    ))
  }

  const updatePersonItem = (personId, itemId, field, value) => {
    setPeople(people.map(person => 
      person.id === personId 
        ? { 
            ...person, 
            items: person.items.map(item => 
              item.id === itemId ? { ...item, [field]: value } : item
            )
          }
        : person
    ))
  }

  const calculatePersonItemsTotal = (person) => {
    if (!person.items || person.items.length === 0) return 0
    return person.items.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0)
  }

  // Handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      setUploadedImage(file)
      
      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  // Process image with OCR simulation
  const processImageOCR = async () => {
    if (!uploadedImage) return

    setIsProcessing(true)
    
    // Simulate OCR processing (dalam implementasi nyata, gunakan Tesseract.js atau API OCR)
    setTimeout(() => {
      // Simulasi hasil OCR dengan contoh data
      const mockOCRResult = {
        items: [
          { name: 'Nasi Goreng Spesial', price: 35000 },
          { name: 'Es Teh Manis', price: 8000 },
          { name: 'Ayam Bakar', price: 45000 },
        ],
        subtotal: 88000,
        tax: 8800,
        total: 96800
      }

      setOcrResult(mockOCRResult)
      
      // Auto-fill form dengan hasil OCR
      setTotalBill(mockOCRResult.subtotal.toString())
      setTax(mockOCRResult.tax.toString())
      
      // Set ke mode detailed dan populate items ke orang pertama
      if (mockOCRResult.items.length > 0) {
        setSplitMethod('detailed')
        setPeople(prev => {
          const updated = [...prev]
          updated[0] = {
            ...updated[0],
            items: mockOCRResult.items.map((item, idx) => ({
              id: Date.now() + idx,
              name: item.name,
              price: item.price.toString()
            }))
          }
          return updated
        })
      }
      
      setIsProcessing(false)
    }, 2000)
  }

  // Clear uploaded image
  const clearImage = () => {
    setUploadedImage(null)
    setImagePreview(null)
    setOcrResult(null)
  }

  const calculateTotal = () => {
    const bill = parseFloat(totalBill) || 0
    const tipAmount = parseFloat(tip) || 0
    const taxAmount = parseFloat(tax) || 0
    const shippingAmount = parseFloat(shipping) || 0
    return bill + tipAmount + taxAmount + shippingAmount
  }

  const calculatePerPerson = () => {
    const total = calculateTotal()
    
    if (splitMethod === 'equal') {
      return total / people.length
    } else if (splitMethod === 'custom') {
      // Custom amounts already defined
      return null
    } else if (splitMethod === 'percentage') {
      // Calculate based on percentage
      const totalPercentage = people.reduce((sum, person) => sum + (parseFloat(person.amount) || 0), 0)
      return people.map(person => {
        const percentage = parseFloat(person.amount) || 0
        return (percentage / totalPercentage) * total
      })
    } else if (splitMethod === 'detailed') {
      // Calculate based on individual items
      return null
    }
  }

  const getPersonAmount = (person, index) => {
    if (splitMethod === 'equal') {
      return calculatePerPerson()
    } else if (splitMethod === 'custom') {
      return parseFloat(person.amount) || 0
    } else if (splitMethod === 'percentage') {
      const amounts = calculatePerPerson()
      return amounts ? amounts[index] : 0
    } else if (splitMethod === 'detailed') {
      return calculatePersonItemsTotal(person)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const clearAll = () => {
    setTotalBill('')
    setTip('')
    setTax('')
    setShipping('')
    setPeople([{ id: 1, name: '', amount: '', items: [] }])
    setSplitMethod('equal')
    clearImage()
  }

  return (
    <div className="min-h-screen bg-white relative overflow-hidden flex flex-col">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-100/30 via-blue-100/30 to-blue-100/30 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gradient-to-tl from-blue-100/30 via-pink-100/30 to-blue-100/30 rounded-full blur-3xl animate-float-delayed"></div>
      </div>

      <main className="relative z-10 p-4 sm:p-6 lg:p-8 flex-1">
      <div className="max-w-6xl mx-auto">
      {/* Header with Gradient - Soft & Elegant */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-slate-50 via-blue-50 to-blue-50 rounded-3xl shadow-md border border-slate-200/60 p-8 mb-6 relative overflow-hidden"
      >
        {/* Soft Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-blue-200/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-blue-200/20 rounded-full blur-3xl animate-pulse delay-300"></div>
        </div>

        <div className="relative z-10 flex items-center gap-4">
          <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500/10 to-blue-500/10 backdrop-blur-sm rounded-2xl shadow-sm border border-blue-200/50">
            <CalculatorIcon className="w-8 h-8 text-blue-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-slate-700 to-blue-600 bg-clip-text text-transparent flex items-center gap-2">
              Split Bill Calculator
              <SparklesIcon className="w-6 h-6 text-blue-500 animate-pulse" />
            </h1>
            <p className="text-slate-600 text-sm mt-1">Bagi tagihan dengan mudah, cepat, dan transparan</p>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Panel - Input */}
        <div className="space-y-6">
          {/* Image Upload Card - Soft & Elegant */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-white rounded-2xl shadow-sm border border-slate-200/60 p-6 hover:shadow-md transition-all duration-300"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-gradient-to-br from-blue-500/10 to-blue-500/10 rounded-xl border border-blue-200/50">
                <CameraIcon className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-blue-600 bg-clip-text text-transparent">
                Upload Struk/Bill
              </h2>
            </div>

            {!imagePreview ? (
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="bill-upload"
                />
                <label
                  htmlFor="bill-upload"
                  className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer bg-slate-50/50 hover:bg-white hover:border-blue-400 transition-all group"
                >
                  <PhotoIcon className="w-12 h-12 text-slate-400 group-hover:text-blue-500 mb-3 transition-colors" />
                  <p className="text-sm font-semibold text-slate-600 group-hover:text-blue-600 transition-colors">
                    📸 Klik untuk upload foto struk
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Format: JPG, PNG, atau HEIC
                  </p>
                </label>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="relative rounded-xl overflow-hidden border border-slate-200">
                  <img
                    src={imagePreview}
                    alt="Uploaded receipt"
                    className="w-full h-48 object-cover"
                  />
                  <button
                    onClick={clearImage}
                    className="absolute top-2 right-2 p-2 bg-white/90 backdrop-blur-sm text-rose-600 rounded-lg hover:bg-rose-50 border border-rose-200 transition-all shadow-sm"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>

                {!ocrResult ? (
                  <button
                    onClick={processImageOCR}
                    disabled={isProcessing}
                    className="w-full py-3 px-6 bg-gradient-to-r from-blue-500 to-blue-500 text-white rounded-xl font-semibold shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 hover:from-blue-600 hover:to-blue-600"
                  >
                    {isProcessing ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                        Memproses...
                      </>
                    ) : (
                      <>
                        <SparklesIcon className="w-5 h-5" />
                        Deteksi Otomatis
                      </>
                    )}
                  </button>
                ) : (
                  <div className="bg-emerald-50/50 border border-emerald-200/60 rounded-xl p-4">
                    <p className="text-emerald-700 font-semibold text-sm mb-2">✅ Berhasil dideteksi!</p>
                    <p className="text-xs text-emerald-600">
                      {ocrResult.items.length} item ditemukan
                    </p>
                  </div>
                )}
              </div>
            )}
          </motion.div>

          {/* Bill Details Card - Soft & Elegant */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl shadow-sm border border-slate-200/60 p-6 hover:shadow-md transition-all duration-300"
          >
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2 bg-gradient-to-br from-blue-500/10 to-blue-500/10 rounded-xl border border-blue-200/50">
                  <BanknotesIcon className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-blue-600 bg-clip-text text-transparent">
                  Detail Tagihan
                </h2>
              </div>
              
              {/* Total Bill */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                  💰 Total Tagihan
                </label>
                <div className="relative group">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">
                    Rp
                  </span>
                  <input
                    type="number"
                    value={totalBill}
                    onChange={(e) => setTotalBill(e.target.value)}
                    placeholder="0"
                    className="w-full pl-12 pr-4 py-4 border border-slate-300 rounded-xl focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:outline-none text-lg font-semibold transition-all hover:border-slate-400 bg-slate-50/50 focus:bg-white"
                  />
                </div>
              </div>

              {/* Tip */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                  🎁 Tip (opsional)
                </label>
                <div className="relative group">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">
                    Rp
                  </span>
                  <input
                    type="number"
                    value={tip}
                    onChange={(e) => setTip(e.target.value)}
                    placeholder="0"
                    className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-xl focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 focus:outline-none transition-all hover:border-slate-400 bg-slate-50/50 focus:bg-white"
                  />
                </div>
              </div>

              {/* Tax */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                  📊 Pajak (opsional)
                </label>
                <div className="relative group">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">
                    Rp
                  </span>
                  <input
                    type="number"
                    value={tax}
                    onChange={(e) => setTax(e.target.value)}
                    placeholder="0"
                    className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-xl focus:border-amber-400 focus:ring-2 focus:ring-amber-100 focus:outline-none transition-all hover:border-slate-400 bg-slate-50/50 focus:bg-white"
                  />
                </div>
              </div>

              {/* Shipping/Ongkir */}
              <div className="mb-5">
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  <div className="flex items-center gap-2">
                    🚚 Ongkos Kirim (opsional)
                  </div>
                </label>
                <div className="relative group">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">
                    Rp
                  </span>
                  <input
                    type="number"
                    value={shipping}
                    onChange={(e) => setShipping(e.target.value)}
                    placeholder="0"
                    className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-xl focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all hover:border-slate-400 bg-slate-50/50 focus:bg-white"
                  />
                </div>
              </div>

              {/* Split Method - Soft & Elegant */}
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                  ⚡ Metode Pembagian
                </label>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <button
                    onClick={() => setSplitMethod('equal')}
                    className={`py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-300 ${
                      splitMethod === 'equal'
                        ? 'bg-gradient-to-br from-blue-500/10 to-blue-500/10 text-blue-700 shadow-sm ring-2 ring-blue-400/50 border border-blue-300'
                        : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    ⚖️ Sama Rata
                  </button>
                  <button
                    onClick={() => setSplitMethod('custom')}
                    className={`py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-300 ${
                      splitMethod === 'custom'
                        ? 'bg-gradient-to-br from-emerald-500/10 to-teal-500/10 text-emerald-700 shadow-sm ring-2 ring-emerald-400/50 border border-emerald-300'
                        : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    ✏️ Custom
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setSplitMethod('percentage')}
                    className={`py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-300 ${
                      splitMethod === 'percentage'
                        ? 'bg-gradient-to-br from-amber-500/10 to-orange-500/10 text-amber-700 shadow-sm ring-2 ring-amber-400/50 border border-amber-300'
                        : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    📊 Persentase
                  </button>
                  <button
                    onClick={() => setSplitMethod('detailed')}
                    className={`py-3 px-4 rounded-xl font-semibold text-sm transition-all duration-300 ${
                      splitMethod === 'detailed'
                        ? 'bg-gradient-to-br from-blue-500/10 to-fuchsia-500/10 text-blue-700 shadow-sm ring-2 ring-blue-400/50 border border-blue-300'
                        : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    🍽️ Per Item
                  </button>
                </div>
              </div>
            </motion.div>

            {/* People Card - Soft & Elegant */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-sm border border-slate-200/60 p-6 hover:shadow-md transition-all duration-300"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-blue-500/10 to-pink-500/10 rounded-xl border border-blue-200/50">
                    <UserGroupIcon className="w-3 h-3 text-blue-600" />
                  </div>
                  <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-pink-600 bg-clip-text text-transparent">
                    Orang ({people.length})
                  </h2>
                </div>
                <button
                  onClick={addPerson}
                  className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-pink-500 text-white rounded-xl hover:from-blue-600 hover:to-pink-600 transition-all shadow-sm hover:shadow-md font-semibold"
                >
                  <PlusIcon className="w-3 h-3" />
                  Tambah Orang
                </button>
              </div>

              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {people.map((person, index) => (
                  <div key={person.id} className="p-4 bg-slate-50/50 rounded-xl border border-slate-200/60 hover:border-slate-300 transition-all">
                    <div className="flex gap-3 items-start mb-3">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={person.name}
                          onChange={(e) => updatePerson(person.id, 'name', e.target.value)}
                          placeholder={`Orang ${index + 1}`}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:outline-none text-sm font-medium bg-white"
                        />
                      </div>
                      {people.length > 1 && (
                        <button
                          onClick={() => removePerson(person.id)}
                          className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors border border-transparent hover:border-rose-200"
                        >
                          <TrashIcon className="w-3 h-3" />
                        </button>
                      )}
                    </div>

                    {splitMethod === 'detailed' && (
                      <div className="space-y-2 mt-3 pl-2 border-l-2 border-blue-300/60">
                        {person.items && person.items.length > 0 && (
                          <div className="space-y-2 mb-2">
                            {person.items.map((item) => (
                              <div key={item.id} className="flex flex-col sm:flex-row gap-2 bg-white p-2 rounded-lg border border-slate-200/60">
                                <input
                                  type="text"
                                  value={item.name}
                                  onChange={(e) => updatePersonItem(person.id, item.id, 'name', e.target.value)}
                                  placeholder="Nama item"
                                  className="flex-1 px-2 py-1.5 border border-slate-300 rounded-lg text-xs focus:border-blue-400 focus:ring-1 focus:ring-blue-100 focus:outline-none"
                                />
                                <div className="flex gap-2 items-center">
                                  <div className="relative flex-1 sm:w-28">
                                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-xs">Rp</span>
                                    <input
                                      type="number"
                                      value={item.price}
                                      onChange={(e) => updatePersonItem(person.id, item.id, 'price', e.target.value)}
                                      placeholder="0"
                                      className="w-full pl-7 pr-2 py-1.5 border border-slate-300 rounded-lg text-xs focus:border-blue-400 focus:ring-1 focus:ring-blue-100 focus:outline-none"
                                    />
                                  </div>
                                  <button
                                    onClick={() => removeItemFromPerson(person.id, item.id)}
                                    className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors border border-transparent hover:border-rose-200 flex-shrink-0"
                                  >
                                    <TrashIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        <button
                          onClick={() => addItemToPerson(person.id)}
                          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-50/80 text-blue-700 rounded-lg hover:bg-blue-100 transition-all text-xs font-medium border border-blue-200/50"
                        >
                          <PlusIcon className="w-3.5 h-3.5" />
                          Tambah Item
                        </button>
                        {person.items && person.items.length > 0 && (
                          <div className="text-right text-xs font-semibold text-blue-700 mt-2 bg-blue-50/50 px-2 py-1 rounded-lg border border-blue-200/50">
                            Total: {formatCurrency(calculatePersonItemsTotal(person))}
                          </div>
                        )}
                      </div>
                    )}

                    {(splitMethod === 'custom' || splitMethod === 'percentage') && (
                      <div className="relative mt-2">
                        <input
                          type="number"
                          value={person.amount}
                          onChange={(e) => updatePerson(person.id, 'amount', e.target.value)}
                          placeholder={splitMethod === 'percentage' ? 'Persentase' : 'Jumlah'}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:outline-none text-sm bg-white"
                        />
                        {splitMethod === 'percentage' && (
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">%</span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Right Panel - Result */}
          <div className="space-y-6">
            {/* Summary Card - Soft & Elegant */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl shadow-sm border border-slate-200/60 p-6 hover:shadow-md transition-all duration-300"
            >
              <div className="bg-gradient-to-br from-blue-50 to-blue-50 rounded-xl p-5 mb-5 shadow-sm border border-blue-100/50">
                <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-600 bg-clip-text text-transparent flex items-center gap-2">
                  <ReceiptPercentIcon className="w-7 h-7 text-blue-600" />
                  Ringkasan Tagihan
                </h2>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-slate-50/50 rounded-xl border border-slate-200/60">
                  <span className="text-slate-700 font-semibold flex items-center gap-2">
                    💵 Subtotal
                  </span>
                  <span className="text-xl font-bold text-slate-800">{formatCurrency(parseFloat(totalBill) || 0)}</span>
                </div>
                {tip && (
                  <div className="flex justify-between items-center p-3 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-100/50">
                    <span className="text-slate-700 font-semibold flex items-center gap-2">
                      🎁 Tip
                    </span>
                    <span className="text-xl font-bold text-emerald-700">{formatCurrency(parseFloat(tip))}</span>
                  </div>
                )}
                {tax && (
                  <div className="flex justify-between items-center p-3 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-100/50">
                    <span className="text-slate-700 font-semibold">📊 Pajak</span>
                    <span className="text-xl font-bold text-amber-700">{formatCurrency(parseFloat(tax))}</span>
                  </div>
                )}
                {shipping && (
                  <div className="flex justify-between items-center p-3 bg-gradient-to-br from-blue-50 to-fuchsia-50 rounded-xl border border-blue-100/50">
                    <span className="text-slate-700 font-semibold flex items-center gap-2">
                      <TruckIcon className="w-4 h-4 text-blue-600" />
                      Ongkir
                    </span>
                    <span className="text-xl font-bold text-blue-700">{formatCurrency(parseFloat(shipping))}</span>
                  </div>
                )}
                <div className="bg-white border-2 border-emerald-400/60 rounded-xl p-5 mt-4 shadow-sm hover:border-emerald-500 hover:shadow-md transition-all">
                  <div className="flex justify-between items-center">
                    <span className="text-xl font-bold text-emerald-700">💎 Total Keseluruhan</span>
                    <span className="text-3xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">{formatCurrency(calculateTotal())}</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Per Person Card - Soft & Elegant */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-sm border border-slate-200/60 p-6 hover:shadow-md transition-all duration-300"
            >
              <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-pink-600 bg-clip-text text-transparent mb-5 flex items-center gap-2">
                <UserGroupIcon className="w-6 h-6 text-blue-600" />
                Pembagian Per Orang
              </h2>
              
              <div className="space-y-3">
                <AnimatePresence>
                {people.map((person, index) => (
                  <motion.div 
                    key={person.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="p-4 bg-gradient-to-br from-blue-50/50 to-pink-50/50 rounded-xl border border-blue-200/60 hover:border-blue-300 transition-all shadow-sm hover:shadow-md"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-bold text-slate-800 flex items-center gap-2">
                          👤 {person.name || `Orang ${index + 1}`}
                        </p>
                        {splitMethod === 'percentage' && person.amount && (
                          <p className="text-xs text-blue-600 font-semibold">{person.amount}%</p>
                        )}
                      </div>
                      <p className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-pink-600 bg-clip-text text-transparent">
                        {formatCurrency(getPersonAmount(person, index))}
                      </p>
                    </div>
                    
                    {/* Show item details in detailed mode */}
                    {splitMethod === 'detailed' && person.items && person.items.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-blue-200/60 space-y-1.5">
                        {person.items.map((item) => (
                          <div key={item.id} className="flex justify-between items-center text-xs">
                            <span className="flex-1 text-slate-600">🍽️ {item.name || 'Item'}</span>
                            <span className="font-semibold text-blue-600">{formatCurrency(parseFloat(item.price) || 0)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                ))}
                </AnimatePresence>
              </div>

              {splitMethod === 'equal' && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-6 p-5 bg-white border-2 border-emerald-400/60 rounded-xl shadow-sm hover:border-emerald-500 hover:shadow-md transition-all"
                >
                  <p className="text-emerald-700 text-center font-bold">
                    ⚡ Per Orang: <span className="text-2xl bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">{formatCurrency(calculatePerPerson())}</span>
                  </p>
                </motion.div>
              )}
            </motion.div>

            {/* Actions - Soft & Elegant */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex gap-3"
            >
              <button
                onClick={clearAll}
                className="flex-1 py-4 px-6 bg-white border border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all font-semibold shadow-sm hover:shadow-md"
              >
                🔄 Reset
              </button>
              <button
                onClick={() => window.print()}
                className="flex-1 py-4 px-6 bg-gradient-to-r from-blue-500 to-blue-500 text-white rounded-xl hover:from-blue-600 hover:to-blue-600 transition-all font-semibold shadow-sm hover:shadow-md"
              >
                🖨️ Print
              </button>
            </motion.div>
          </div>
        </div>
      </div>
      </main>
      <Footer />
    </div>
  )
}
